/**
 * Provides an administration interface for managing widget display names
 * and localization content across the application. Allows administrators
 * to view, edit, and maintain English and French translations for widgets,
 * with support for change tracking, save operations, and reverting
 * unsaved modifications.
 */
import React, { useEffect, useState } from "react";

import { getWidgetNames, updateWidgetNames } from "@/services/commonconfig";

const WidgetNameConfig: React.FC = () => {
  const [widgets, setWidgets] = useState<any[]>([]);
  const [initial, setInitial] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    const data = await getWidgetNames();

    setWidgets(data);
    setInitial(JSON.stringify(data));
  };

  const hasChanges = initial !== "" && JSON.stringify(widgets) !== initial;

  const handleSave = async () => {
    try {
      setSaving(true);

      await updateWidgetNames(widgets);

      setInitial(JSON.stringify(widgets));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setWidgets(JSON.parse(initial));
  };

  const updateEnglish = (index: number, value: string) => {
    setWidgets((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        en: value,
      };

      return updated;
    });
  };

  const updateFrench = (index: number, value: string) => {
    setWidgets((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        fr: value,
      };

      return updated;
    });
  };

  return (
    <div className="border border-gray-200 rounded-md bg-[#fbfaf7] p-3 space-y-4">
      <div>
        <p className="text-xs text-gray-600">
          Configure widget names displayed throughout the application.
        </p>
      </div>

      <div className="grid grid-cols-[450px_1fr_1fr] gap-3 text-xs font-semibold text-gray-600 border-b pb-2">
        <div>Translation Key</div>
        <div>English</div>
        <div>French</div>
      </div>

      {widgets.map((widget, index) => (
        <div
          key={`${widget.translation_key}-${index}`}
          className="grid grid-cols-[450px_1fr_1fr] gap-3 items-center text-xs"
        >
          <div className="font-medium text-gray-700 break-all">{widget.translation_key}</div>

          <input
            type="text"
            value={widget.en ?? ""}
            onChange={(e) => updateEnglish(index, e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          />

          <input
            type="text"
            value={widget.fr ?? ""}
            onChange={(e) => updateFrench(index, e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-1 focus:ring-[var(--color-brand)]"
          />
        </div>
      ))}

      <div className="flex justify-end gap-2 items-center">
        {hasChanges && (
          <div className="text-orange-600 border border-orange-300 bg-orange-50 px-2 py-0.5 rounded text-xs">
            Unsaved Changes
          </div>
        )}

        <button
          className="bg-[#e7e5e0] px-3 py-1 rounded text-xs"
          onClick={handleCancel}
          disabled={!hasChanges || saving}
        >
          Cancel
        </button>

        <button
          className="bg-[var(--color-brand)] text-white px-3 py-1 rounded text-xs"
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default WidgetNameConfig;
